import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  TextField,
  IconButton,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  RefreshOutlined,
  SearchOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@mui/icons-material";
import GetAppIcon from "@mui/icons-material/GetApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DescriptionIcon from "@mui/icons-material/Description";
import TableViewIcon from "@mui/icons-material/TableView";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "../../utils/axiosInstance";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MapIcon from "@mui/icons-material/Map";

const MapOverview = ({}) => {
  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [loadingUnitSearchData, setLoadingUnitSearchData] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isClusterEnabled, setIsClusterEnabled] = useState(true);

  // Export menu state
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

  const handleExportMenuOpen = useCallback((event) => {
    setExportMenuAnchor(event.currentTarget);
  }, []);

  const handleExportMenuClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);

  const exportToCSV = useCallback(() => {
    const csvContent = [
      ["Unit Name", "Modem Serial Number", "Latitude", "Longitude", "Status"],
      ...filteredUnits.map((unit) => [
        unit.name || `Unit ${unit.id}`,
        unit.id,
        unit.latitude != null ? Number(unit.latitude).toFixed(6) : "",
        unit.longitude != null ? Number(unit.longitude).toFixed(6) : "",
        unit.status,
      ]),
    ];

    const csvString = csvContent
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `units_map_data.csv`;
    link.click();

    handleExportMenuClose();
  }, [filteredUnits, handleExportMenuClose]);

  const exportToExcel = useCallback(() => {
    const headers = [
      "Unit Name",
      "Modem Serial Number",
      "Latitude",
      "Longitude",
      "Status",
    ];
    const rows = filteredUnits.map((unit) => [
      unit.name || `Unit ${unit.id}`,
      unit.id,
      unit.latitude != null ? Number(unit.latitude).toFixed(6) : "",
      unit.longitude != null ? Number(unit.longitude).toFixed(6) : "",
      unit.status,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Units");
    XLSX.writeFile(workbook, `units_map_data.xlsx`);

    handleExportMenuClose();
  }, [filteredUnits, handleExportMenuClose]);

  const exportToPDF = useCallback(() => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Units Map Report", 14, 20);

    const headers = [
      ["Unit Name", "Modem Serial Number", "Latitude", "Longitude", "Status"],
    ];
    const rows = filteredUnits.map((unit) => [
      unit.name || `Unit ${unit.id}`,
      unit.id,
      unit.latitude != null ? Number(unit.latitude).toFixed(6) : "",
      unit.longitude != null ? Number(unit.longitude).toFixed(6) : "",
      unit.status,
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 30,
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`units_map_data.pdf`);
    handleExportMenuClose();
  }, [filteredUnits, handleExportMenuClose]);

  const copyToClipboard = useCallback(() => {
    const headers =
      "Unit Name\tModem Serial Number\tLatitude\tLongitude\tStatus";
    const textContent = filteredUnits
      .map(
        (unit) =>
          `${unit.name || `Unit ${unit.id}`}\t${unit.id}\t${unit.latitude != null ? Number(unit.latitude).toFixed(6) : ""}\t${unit.longitude != null ? Number(unit.longitude).toFixed(6) : ""}\t${unit.status}`,
      )
      .join("\n");

    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => console.log("Data copied to clipboard"))
      .catch((err) => console.error("Failed to copy:", err));

    handleExportMenuClose();
  }, [filteredUnits, handleExportMenuClose]);

  const isOlderThan24Hours = (sampleTime) => {
    if (!sampleTime) return true;

    try {
      const sampleDate = new Date(sampleTime);
      const currentDate = new Date();
      const diffInHours = (currentDate - sampleDate) / (1000 * 60 * 60);
      return diffInHours > 24;
    } catch (error) {
      console.error("Error parsing date:", error);
      return true;
    }
  };
  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
  }, []);

  const handleFullScreenToggle = () => {
    const mapElement = mapRef.current;
    if (!mapElement) return;

    if (!document.fullscreenElement) {
      mapElement.requestFullscreen().catch((err) => {
        console.error(`Error enabling fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markerClusterGroupRef = useRef(null);
  const leafletRef = useRef(null);
  const individualMarkersRef = useRef([]);

  const colors = ["#2196f3"];

  // Fetch units from API
  useEffect(() => {
    const fetchUnits = async () => {
      try {
        setLoadingUnitSearchData(true);

        const devicesRes = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/alldeviceslatlon?companyId=${process.env.REACT_APP_COMPANY_ID}`,
        );
        const devices = devicesRes.data || [];

        const statusRes = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/iot/specific-area-status`,
          {
            params: {
              companyId: process.env.REACT_APP_COMPANY_ID,
            },
          },
        );
        const statusData = statusRes.data?.data || [];

        const findStatus = (unitId) => statusData.find((s) => s.id === unitId);

        const mappedData = devices.map((unit) => {
          const statusInfo = findStatus(unit.id);
          let status = "offline";

          if (statusInfo) {
            if (unit.id === "11223344SKY1111") {
              console.log("statusInfo", statusInfo);
            }
            const { time, stateDesc } = statusInfo;
            if (time != "N/A" && !isOlderThan24Hours(time)) {
              status = "online";
            } else if (stateDesc === "SHUTDOWN") {
              status = "alarm";
            } else {
              status = "offline";
            }
          }

          return {
            id: unit.id,
            name: unit.name,
            latitude: unit.latitude,
            longitude: unit.longitude,
            macaddr: unit.macaddr,
            groupId: unit.groupId,
            status,
          };
        });

        setUnits(mappedData);
        setFilteredUnits(mappedData);
      } catch (err) {
        console.error("❌ Error fetching devices or statuses:", err);
        setUnits([]);
        setFilteredUnits([]);
      } finally {
        setLoadingUnitSearchData(false);
      }
    };

    fetchUnits();
  }, []);

  // Debounce search input by 300ms to avoid re-rendering on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filter units based on debounced search term
  useEffect(() => {
    if (debouncedSearch.trim()) {
      const filtered = units.filter(
        (unit) =>
          unit.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          unit.id
            ?.toString()
            .toLowerCase()
            .includes(debouncedSearch.toLowerCase()),
      );
      setFilteredUnits(filtered);
    } else {
      setFilteredUnits(units);
    }
  }, [debouncedSearch, units]);

  // Load Leaflet and MarkerCluster plugin
  useEffect(() => {
    let isCancelled = false;

    const loadLeaflet = async () => {
      try {
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const leafletCSS = document.createElement("link");
          leafletCSS.rel = "stylesheet";
          leafletCSS.href =
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css";
          document.head.appendChild(leafletCSS);
        }

        if (!document.querySelector('link[href*="MarkerCluster"]')) {
          const clusterCSS = document.createElement("link");
          clusterCSS.rel = "stylesheet";
          clusterCSS.href =
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.css";
          document.head.appendChild(clusterCSS);

          const clusterDefaultCSS = document.createElement("link");
          clusterDefaultCSS.rel = "stylesheet";
          clusterDefaultCSS.href =
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/MarkerCluster.Default.css";
          document.head.appendChild(clusterDefaultCSS);
        }

        if (!window.L) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
              "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!window.L.markerClusterGroup) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src =
              "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/leaflet.markercluster.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!isCancelled) {
          leafletRef.current = window.L;
          initializeMap();
        }
      } catch (error) {
        console.error("Error loading Leaflet:", error);
      }
    };

    if (mapRef.current && !leafletMapRef.current) {
      loadLeaflet();
    }

    return () => {
      isCancelled = true;
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []);

  // Update markers when units data changes
  useEffect(() => {
    if (isMapLoaded && units.length > 0) {
      addMarkersToMap();
    }
  }, [filteredUnits, isMapLoaded, isClusterEnabled]);

  const initializeMap = () => {
    if (!leafletRef.current || !mapRef.current || leafletMapRef.current) return;

    const L = leafletRef.current;

    if (L.Icon.Default.prototype._getIconUrl) {
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
        iconUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
        shadowUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
      });
    }

    leafletMapRef.current = L.map(mapRef.current, {
      center: [39.8283, -98.5795],
      zoom: 5,
      zoomControl: true,
      minZoom: 2,
      maxZoom: 18,
      maxBounds: [
        [-90, -180],
        [90, 180],
      ],
      maxBoundsViscosity: 1.0,
    });

    const satelliteLayer = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { attribution: "", maxZoom: 19 },
    );

    const labelsLayer = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { attribution: "", maxZoom: 19, opacity: 0.8 },
    );

    satelliteLayer.addTo(leafletMapRef.current);
    labelsLayer.addTo(leafletMapRef.current);

    markerClusterGroupRef.current = L.markerClusterGroup({
      maxClusterRadius: 150,
      iconCreateFunction: function (cluster) {
        const count = cluster.getChildCount();
        let size = "small";
        let className = "marker-cluster-small";

        if (count < 30) {
          size = "small";
          className = "marker-cluster-small";
        } else if (count < 80) {
          size = "medium";
          className = "marker-cluster-medium";
        } else {
          size = "large";
          className = "marker-cluster-large";
        }

        return new L.DivIcon({
          html: `<div><span>${count}</span></div>`,
          className: `marker-cluster ${className}`,
          iconSize: new L.Point(40, 40),
        });
      },
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    });

    leafletMapRef.current.addLayer(markerClusterGroupRef.current);

    if (!document.querySelector("#cluster-styles")) {
      const style = document.createElement("style");
      style.id = "cluster-styles";
      style.textContent = `
                .marker-cluster-small {
                    background-color: rgba(181, 226, 140, 0.6);
                    border: 2px solid rgba(110, 204, 57, 1);
                }
                .marker-cluster-small div {
                    background-color: rgba(110, 204, 57, 0.8);
                }
                .marker-cluster-medium {
                    background-color: rgba(241, 211, 87, 0.6);
                    border: 2px solid rgba(240, 194, 12, 1);
                }
                .marker-cluster-medium div {
                    background-color: rgba(240, 194, 12, 0.8);
                }
                .marker-cluster-large {
                    background-color: rgba(253, 156, 115, 0.6);
                    border: 2px solid rgba(241, 128, 23, 1);
                }
                .marker-cluster-large div {
                    background-color: rgba(241, 128, 23, 0.8);
                }
                .marker-cluster {
                    border-radius: 50%;
                    text-align: center;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    font-weight: bold;
                }
                .marker-cluster div {
                    width: 30px;
                    height: 30px;
                    margin-left: 5px;
                    margin-top: 5px;
                    text-align: center;
                    border-radius: 50%;
                    font: 12px "Helvetica Neue", Arial, Helvetica, sans-serif;
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .marker-cluster span {
                    line-height: 1;
                }
            `;
      document.head.appendChild(style);
    }

    setIsMapLoaded(true);
  };

  const addMarkersToMap = () => {
    if (
      !leafletMapRef.current ||
      !leafletRef.current ||
      !markerClusterGroupRef.current
    )
      return;

    const L = leafletRef.current;

    markerClusterGroupRef.current.clearLayers();
    individualMarkersRef.current.forEach((marker) =>
      leafletMapRef.current.removeLayer(marker),
    );
    individualMarkersRef.current = [];

    filteredUnits.forEach((unit, index) => {
      const lat =
        unit.latitude && unit.latitude !== ""
          ? parseFloat(unit.latitude)
          : null;
      const lng =
        unit.longitude && unit.longitude !== ""
          ? parseFloat(unit.longitude)
          : null;

      const icon = L.divIcon({
        className: "custom-div-icon",
        html: `<div style="background-color: ${
          colors[index % colors.length]
        }; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.4);"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      const marker = L.marker([lat, lng], { icon }).bindPopup(
        `<strong>${unit.name || unit.id}</strong><br/>ID: ${
          unit.id
        }<br/>Lat: ${lat.toFixed(6)}<br/>Lng: ${lng.toFixed(6)}`,
      );

      if (isClusterEnabled) {
        markerClusterGroupRef.current.addLayer(marker);
      } else {
        marker.addTo(leafletMapRef.current);
        individualMarkersRef.current.push(marker);
      }
    });

    if (filteredUnits.length > 0) {
      const group = isClusterEnabled
        ? new L.featureGroup(markerClusterGroupRef.current.getLayers())
        : new L.featureGroup(individualMarkersRef.current);
      if (group.getLayers().length > 0) {
        leafletMapRef.current.fitBounds(group.getBounds().pad(0.2));
      }
    }
  };

  const gotoUnitMap = (unitId) => {
    const unit = units.find((u) => u.id === unitId);
    if (unit && leafletMapRef.current) {
      const lat =
        unit.latitude && unit.latitude !== "" ? parseFloat(unit.latitude) : 0;
      const lng =
        unit.longitude && unit.longitude !== ""
          ? parseFloat(unit.longitude)
          : 0;
      leafletMapRef.current.setView([lat, lng], 15);

      markerClusterGroupRef.current.eachLayer(function (layer) {
        if (
          layer.getPopup() &&
          layer.getPopup().getContent().includes(`ID: ${unitId}`)
        ) {
          layer.openPopup();
        }
      });
    }
  };

  const refreshMap = () => {
    if (leafletMapRef.current && markerClusterGroupRef.current) {
      setSearchTerm("");
      setDebouncedSearch("");
      setFilteredUnits(units);

      addMarkersToMap();

      const bounds = markerClusterGroupRef.current.getBounds();
      if (bounds.isValid()) {
        leafletMapRef.current.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  };

  return (
    <Box sx={{ bgcolor: "#1e293b" }}>
      {/* Main Container */}
      <Box
        sx={{
          p: {
            xs: 1.5,
            sm: 3,
            md: 3,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: {
              xs: 1,
              sm: 3,
              md: 3,
            },
            height: {
              xs: "auto",
              sm: "80vh",
              md: "80vh",
            },
            minHeight: "600px",
            flexDirection: {
              xs: "column",
              sm: "row",
              md: "row",
            },
          }}
        >
          {/* Map Container */}
          <Card
            sx={{
              flex: {
                xs: 1,
                sm: "0 0 56%",
                md: "0 0 56%",
              },
              display: "flex",
              flexDirection: "column",
              minHeight: {
                xs: "200px",
                sm: "500px",
                md: "auto",
              },
              height: {
                xs: "40vh",
                sm: "80%",
                md: "100%",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: {
                  xs: 0,
                  sm: 2,
                  md: 2,
                },
                borderBottom: "1px solid #0f7915ff",
                bgcolor: "#0f7915ff",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: {
                    xs: 300,
                    sm: 500,
                    md: 600,
                  },
                  fontSize: {
                    xs: "14px",
                    sm: "20px",
                    md: "20px",
                  },
                  padding: {
                    xs: "5px",
                    sm: "0px",
                    md: "0px",
                  },
                  color: "white",
                }}
              >
                Map View
              </Typography>
              <Box display="flex" alignItems={"center"}>
                <Box display="flex" gap={1}>
                  <Button
                    size="small"
                    variant={isClusterEnabled ? "contained" : "outlined"}
                    onClick={() => setIsClusterEnabled(true)}
                    startIcon={<MapIcon />}
                    sx={{
                      color: isClusterEnabled ? "#000" : "#fff",
                      borderColor: "#fff",
                      bgcolor: isClusterEnabled ? "#fff" : "transparent",
                      "&:hover": {
                        borderColor: "#fff",
                        bgcolor: isClusterEnabled
                          ? "#e0e0e0"
                          : "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Group Map
                  </Button>
                  <Button
                    size="small"
                    variant={!isClusterEnabled ? "contained" : "outlined"}
                    onClick={() => setIsClusterEnabled(false)}
                    startIcon={<LocationOnIcon />}
                    sx={{
                      color: !isClusterEnabled ? "#000" : "#fff",
                      borderColor: "#fff",
                      bgcolor: !isClusterEnabled ? "#fff" : "transparent",
                      "&:hover": {
                        borderColor: "#fff",
                        bgcolor: !isClusterEnabled
                          ? "#e0e0e0"
                          : "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Marker Map
                  </Button>
                </Box>
                <Box>
                  <IconButton
                    size="small"
                    title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
                    onClick={handleFullScreenToggle}
                    sx={{
                      color: "white",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    {isFullScreen ? (
                      <FullscreenExitOutlined />
                    ) : (
                      <FullscreenOutlined />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </Box>

            <Box sx={{ flex: 1, position: "relative" }}>
              <Box
                ref={mapRef}
                sx={{
                  position: "relative",
                  width: "100%",
                  height: "100%",
                  minHeight: "350px",
                }}
              />
              {!isMapLoaded && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    color: "text.secondary",
                    fontSize: "16px",
                  }}
                >
                  Loading satellite map with clustering...
                </Box>
              )}
            </Box>
          </Card>

          {/* Units Container */}
          <Card
            sx={{
              flex: {
                xs: "none",
                sm: "0 0 44%",
                md: "0 0 44%",
              },
              display: "flex",
              flexDirection: "column",
              height: {
                xs: "calc(41.5vh - 60px)",
                sm: "80%",
                md: "auto",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: {
                  xs: 0,
                  sm: 2,
                  md: 2,
                },
                borderBottom: "1px solid #131010ff",
                bgcolor: "#0f7915ff",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: {
                    xs: 300,
                    sm: 500,
                    md: 600,
                  },
                  fontSize: {
                    xs: "14px",
                    sm: "20px",
                    md: "20px",
                  },
                  padding: {
                    xs: "5px",
                    sm: "0px",
                    md: "0px",
                  },
                  color: "white",
                }}
              >
                Units ({filteredUnits.length})
              </Typography>
              <IconButton
                size="small"
                title="View All Units"
                onClick={refreshMap}
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <RefreshOutlined />
              </IconButton>
            </Box>

            {/* Search + Export Row */}
            <Box
              sx={{
                p: {
                  xs: 1,
                  sm: 2,
                  md: 2,
                },
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search units by name or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
              <Button
                color="white"
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={handleExportMenuOpen}
                sx={{
                  borderRadius: 0,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  fontSize: { xs: "10.5px", sm: "14px", md: "15px" },
                }}
              >
                Export
              </Button>
            </Box>

            {/* Export Menu */}
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={handleExportMenuClose}
            >
              <MenuItem onClick={copyToClipboard}>
                <ListItemIcon>
                  <FileCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToCSV}>
                <ListItemIcon>
                  <DescriptionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>CSV</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToExcel}>
                <ListItemIcon>
                  <TableViewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Excel</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToPDF}>
                <ListItemIcon>
                  <PictureAsPdfIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>PDF</ListItemText>
              </MenuItem>
            </Menu>

            {/* Units List */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Sticky Header Row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "34% 32% 34%",
                  bgcolor: "black",
                  flexShrink: 0,
                }}
              >
                {["Unit Name", "Modem Serial Number", "Coordinates"].map(
                  (label) => (
                    <Box
                      key={label}
                      sx={{
                        color: "white",
                        fontWeight: 500,
                        fontSize: { xs: "11px", sm: "13px", sm: "13px" },
                        textAlign: "center",
                        padding: { xs: "6px 4px", sm: "10px" },
                        borderRight: "1px solid #222",
                        "&:last-child": { borderRight: "none" },
                      }}
                    >
                      {label}
                    </Box>
                  ),
                )}
              </Box>

              {/* Body */}
              {loadingUnitSearchData ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Loading units...
                  </Typography>
                </Box>
              ) : filteredUnits.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: "italic" }}
                  >
                    {searchTerm
                      ? "No units found matching your search"
                      : "No units available"}
                  </Typography>
                </Box>
              ) : (
                <div style={{ overflowY: "auto", flex: 1 }}>
                  {filteredUnits.map((unit, index) => (
                    <div
                      key={unit.id}
                      onClick={() => gotoUnitMap(unit.id)}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "32% 38% 30%",
                        alignItems: "center",
                        height: "46px",
                        cursor: "pointer",
                        backgroundColor:
                          index % 2 === 0 ? "#2f3136" : "#3a3c43",
                        borderBottom: "1px solid #25272c",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#4a4d54")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor =
                          index % 2 === 0 ? "#2f3136" : "#3a3c43")
                      }
                    >
                      {/* Unit Name */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "0 4px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            flexShrink: 0,
                            backgroundColor:
                              unit.status === "online"
                                ? "#22c55e"
                                : unit.status === "alarm"
                                  ? "#ef4444"
                                  : "#9ca3af",
                          }}
                        />
                        <span
                          style={{
                            fontSize:
                              window.innerWidth < 600
                                ? "8px"
                                : window.innerWidth < 900
                                  ? "10px"
                                  : "11px",
                            color: "#90caf9",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {unit.name || `Unit ${unit.id}`}
                        </span>
                      </div>

                      {/* Modem Serial */}
                      <div
                        style={{
                          textAlign: "center",
                          padding: "0 5px",
                          overflow: "hidden",
                        }}
                      >
                        <span
                          style={{
                            fontSize:
                              window.innerWidth < 600
                                ? "8px"
                                : window.innerWidth < 900
                                  ? "10px"
                                  : "11px",
                            color: "#9ca3af",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {unit.id}
                        </span>
                      </div>

                      {/* Coordinates */}
                      <div style={{ padding: "0 8px", overflow: "hidden" }}>
                        <span
                          style={{
                            fontSize:
                              window.innerWidth < 600
                                ? "8px"
                                : window.innerWidth < 900
                                  ? "10px"
                                  : "11px",
                            color: "#9ca3af",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                          }}
                        >
                          {unit.latitude != null
                            ? Number(unit.latitude).toFixed(6)
                            : "—"}
                          ,&nbsp;
                          {unit.longitude != null
                            ? Number(unit.longitude).toFixed(6)
                            : "—"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default MapOverview;
